import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiLayers, FiPlus, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { getLocale, t, useI18n } from '../../i18n';
import { payBandService } from '../../services/payBandService';
import { payrollService } from '../../services/payrollService';
import type { EmployeePayAssignment, EmploymentType, PayBand } from '../../services/payroll/types';
import './PayrollWorkspace.css';

const EMPLOYMENT_TYPES: EmploymentType[] = ['full_time', 'part_time', 'relief', 'contractor', 'apprentice'];

const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  full_time: 'Full time',
  part_time: 'Part time',
  relief: 'Relief',
  contractor: 'Contractor',
  apprentice: 'Apprentice',
};

function todayKey(): string {
  const now = new Date();
  return [
    String(now.getFullYear()),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function normaliseCurrencyCode(value: string | null | undefined): string {
  const candidate = value?.trim().toUpperCase() ?? '';
  return /^[A-Z]{3}$/.test(candidate) ? candidate : 'EUR';
}

function formatMoney(value: number | null, currency: string): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDateRange(from: string, to: string | null): string {
  return to ? `${from} → ${to}` : t('{from} → open', { from });
}

export default function PayBandsPage() {
  useI18n();

  const [bands, setBands] = useState<PayBand[]>([]);
  const [assignments, setAssignments] = useState<EmployeePayAssignment[]>([]);
  const [unbanded, setUnbanded] = useState<{ id: string; fullName: string }[]>([]);
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [selectedBandId, setSelectedBandId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [showBandForm, setShowBandForm] = useState(false);
  const [bandCode, setBandCode] = useState('');
  const [bandName, setBandName] = useState('');
  const [bandRate, setBandRate] = useState('16.15');

  const [rateValue, setRateValue] = useState('');
  const [rateFrom, setRateFrom] = useState(todayKey());

  const [assignGuardId, setAssignGuardId] = useState('');
  const [assignBandId, setAssignBandId] = useState('');
  const [assignFrom, setAssignFrom] = useState(todayKey());
  const [assignOverride, setAssignOverride] = useState('');
  const [assignEmploymentType, setAssignEmploymentType] = useState<EmploymentType>('full_time');
  const [assignReference, setAssignReference] = useState('');
  const [assignCostCentre, setAssignCostCentre] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [nextBands, nextAssignments, nextUnbanded, settings] = await Promise.all([
        payBandService.listPayBands(),
        payBandService.listAssignments(),
        payBandService.listUnbandedGuards(),
        payrollService.fetchPayrollSettings(),
      ]);
      setBands(nextBands);
      setAssignments(nextAssignments);
      setUnbanded(nextUnbanded);
      setCurrencyCode(normaliseCurrencyCode(settings.currencyCode));
      setSelectedBandId((current) => current ?? nextBands[0]?.id ?? null);
      if (nextBands.length && !assignBandId) setAssignBandId(nextBands[0].id);
    } catch (err) {
      setError((err as Error).message || t('Failed to load pay bands'));
    } finally {
      setIsLoading(false);
    }
    // assignBandId is seeded once; re-running on every change would fight the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const selectedBand = useMemo(
    () => bands.find((band) => band.id === selectedBandId) ?? null,
    [bands, selectedBandId],
  );

  const assignmentsForBand = useMemo(
    () => assignments.filter((assignment) => assignment.payBandId === selectedBandId),
    [assignments, selectedBandId],
  );

  const assignableGuards = useMemo(() => {
    const byId = new Map(unbanded.map((guard) => [guard.id, guard] as const));
    assignments.forEach((assignment) => {
      if (!byId.has(assignment.guardId)) {
        byId.set(assignment.guardId, {
          id: assignment.guardId,
          fullName: assignment.guardName,
        });
      }
    });
    return [...byId.values()].sort((left, right) => (
      left.fullName.localeCompare(right.fullName, getLocale())
    ));
  }, [assignments, unbanded]);

  const runAction = async (action: () => Promise<void>, successMessage: string) => {
    setIsSaving(true);
    setError('');
    setMessage('');
    try {
      await action();
      setMessage(successMessage);
      await load();
    } catch (err) {
      setError((err as Error).message || t('Something went wrong'));
    } finally {
      setIsSaving(false);
    }
  };

  const createBand = () => runAction(async () => {
    if (!bandCode.trim() || !bandName.trim()) throw new Error(t('Give the pay band a code and a name.'));
    const parsedRate = Number(bandRate);
    if (!bandRate.trim() || !Number.isFinite(parsedRate) || parsedRate < 0) {
      throw new Error(t('Enter a valid hourly rate.'));
    }
    await payBandService.createPayBand(
      { code: bandCode, name: bandName },
      parsedRate,
    );
    setBandCode('');
    setBandName('');
    setShowBandForm(false);
  }, t('Pay band created.'));

  const addRate = () => runAction(async () => {
    if (!selectedBandId) throw new Error(t('Select a pay band first.'));
    const parsed = Number(rateValue);
    if (!Number.isFinite(parsed) || parsed < 0) throw new Error(t('Enter a valid hourly rate.'));
    await payBandService.addRate({
      payBandId: selectedBandId,
      effectiveFrom: rateFrom,
      baseHourlyRate: parsed,
    });
    setRateValue('');
  }, t('New rate scheduled.'));

  const assignEmployee = () => runAction(async () => {
    if (!assignGuardId) throw new Error(t('Choose an employee to assign.'));
    if (!assignBandId) throw new Error(t('Choose a pay band.'));
    await payBandService.assignEmployee({
      guardId: assignGuardId,
      payBandId: assignBandId,
      effectiveFrom: assignFrom,
      hourlyRateOverride: assignOverride ? Number(assignOverride) : null,
      employmentType: assignEmploymentType,
      payrollReference: assignReference || null,
      costCentre: assignCostCentre || null,
    });
    setAssignGuardId('');
    setAssignOverride('');
    setAssignReference('');
    setAssignCostCentre('');
  }, t('Employee assigned to pay band.'));

  return (
    <div className="pay-page">
      <div className="pay-page__header" data-tour="payroll-bands-header">
        <div className="pay-page__heading">
          <h1 className="pay-page__title">{t('Pay bands')}</h1>
          <p className="pay-page__subtitle">
            {t('Group employees into pay grades with their own rates. Rates are effective dated, so historic payroll runs keep reporting what was actually paid.')}
          </p>
        </div>
        <div className="pay-page__actions">
          <button type="button" className="btn btn--secondary" onClick={() => { void load(); }} disabled={isLoading}>
            <FiRefreshCw aria-hidden="true" />
            {t('Refresh')}
          </button>
          <button type="button" className="btn btn--primary" onClick={() => setShowBandForm((value) => !value)}>
            <FiPlus aria-hidden="true" />
            {t('New pay band')}
          </button>
        </div>
      </div>

      {(error || message) && (
        <div className="admin-account-page__messages">
          {error && <div className="msg--error">{error}</div>}
          {message && <div className="msg--success">{message}</div>}
        </div>
      )}

      {unbanded.length > 0 && (
        <section className="pay-warnings">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <p>
              {unbanded.length === 1
                ? t('1 employee has no pay band and will fall back to the company base rate.')
                : t('{count} employees have no pay band and will fall back to the company base rate.', { count: unbanded.length })}
            </p>
            <p className="pay-table__muted pay-truncate-list">{unbanded.map((guard) => guard.fullName).join(', ')}</p>
          </div>
        </section>
      )}

      {showBandForm && (
        <section className="pay-panel">
          <div className="pay-panel__head">
            <div>
              <h2>{t('New pay band')}</h2>
              <p>{t('The opening rate starts today. Schedule future changes from the rate history.')}</p>
            </div>
          </div>
          <div className="pay-form-grid">
            <label className="form__group">
              <span className="form__label">{t('Code')}</span>
              <input
                className="form__input"
                value={bandCode}
                maxLength={32}
                placeholder="STATIC_T1"
                onChange={(event) => setBandCode(event.target.value)}
              />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Name')}</span>
              <input
                className="form__input"
                value={bandName}
                placeholder={t('Static Guard Tier 1')}
                onChange={(event) => setBandName(event.target.value)}
              />
            </label>
            <label className="form__group">
              <span className="form__label">{t('Opening hourly rate')}</span>
              <input
                className="form__input"
                type="number"
                min="0"
                step="0.01"
                value={bandRate}
                onChange={(event) => setBandRate(event.target.value)}
              />
            </label>
          </div>
          <div className="pay-inline-actions" style={{ marginTop: 14 }}>
            <button type="button" className="btn btn--primary" onClick={createBand} disabled={isSaving}>
              {t('Create pay band')}
            </button>
            <button type="button" className="btn btn--secondary" onClick={() => setShowBandForm(false)}>
              {t('Cancel')}
            </button>
          </div>
        </section>
      )}

      <div className="pay-split pay-split--wide">
        <section className="pay-panel">
          <div className="pay-panel__head" data-tour="payroll-bands-list">
            <span className="admin-account-page__panel-icon"><FiLayers aria-hidden="true" /></span>
            <div style={{ flex: 1 }}>
              <h2>{t('Bands')}</h2>
              <p>{t('Select a band to see its rate history and assigned employees.')}</p>
            </div>
          </div>

          {isLoading ? (
            <div className="pay-empty">{t('Loading pay bands...')}</div>
          ) : bands.length === 0 ? (
            <div className="pay-empty">{t('No pay bands yet. Create one to get started.')}</div>
          ) : (
            <div className="pay-table-wrap">
              <table className="pay-table">
                <thead>
                  <tr>
                    <th>{t('Code')}</th>
                    <th>{t('Name')}</th>
                    <th className="pay-table__num">{t('Current rate')}</th>
                    <th className="pay-table__num">{t('Employees')}</th>
                  </tr>
                </thead>
                <tbody>
                  {bands.map((band) => (
                    <tr
                      key={band.id}
                      style={{ background: band.id === selectedBandId ? 'rgba(255,255,255,0.04)' : undefined }}
                    >
                      <td>
                        <button
                          type="button"
                          className="pay-table__row-button"
                          onClick={() => setSelectedBandId(band.id)}
                        >
                          {band.code}
                        </button>
                      </td>
                      <td>
                        {band.name}
                        {band.isDefault && <span className="pay-chip pay-chip--muted" style={{ marginLeft: 8 }}>{t('Default')}</span>}
                        {!band.isActive && <span className="pay-chip pay-chip--warn" style={{ marginLeft: 8 }}>{t('Inactive')}</span>}
                      </td>
                      <td className="pay-table__money">
                        {formatMoney(band.currentRate?.baseHourlyRate ?? null, currencyCode)}
                      </td>
                      <td className="pay-table__num">{band.assignedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="pay-panel">
          <div className="pay-panel__head">
            <div style={{ flex: 1 }}>
              <h2>{selectedBand ? t('{name} rate history', { name: selectedBand.name }) : t('Rate history')}</h2>
              <p>{t('Adding a rate closes the previous one the day before, so the windows never overlap.')}</p>
            </div>
          </div>

          {!selectedBand ? (
            <div className="pay-empty">{t('Select a pay band to see its rates.')}</div>
          ) : (
            <>
              <div className="pay-table-wrap" style={{ marginBottom: 16 }}>
                <table className="pay-table">
                  <thead>
                    <tr>
                      <th>{t('Effective')}</th>
                      <th className="pay-table__num">{t('Hourly rate')}</th>
                      <th>{t('Note')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBand.rates.length === 0 ? (
                      <tr><td colSpan={3} className="pay-empty">{t('No rates recorded.')}</td></tr>
                    ) : selectedBand.rates.map((rate) => (
                      <tr key={rate.id}>
                        <td>{formatDateRange(rate.effectiveFrom, rate.effectiveTo)}</td>
                        <td className="pay-table__money">{formatMoney(rate.baseHourlyRate, currencyCode)}</td>
                        <td className="pay-table__muted">{rate.note ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pay-form-row">
                <label className="form__group">
                  <span className="form__label">{t('New rate')}</span>
                  <input
                    className="form__input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={rateValue}
                    onChange={(event) => setRateValue(event.target.value)}
                  />
                </label>
                <label className="form__group">
                  <span className="form__label">{t('Effective from')}</span>
                  <input
                    className="form__input"
                    type="date"
                    value={rateFrom}
                    onChange={(event) => setRateFrom(event.target.value)}
                  />
                </label>
                <button type="button" className="btn btn--secondary" onClick={addRate} disabled={isSaving}>
                  {t('Schedule rate')}
                </button>
              </div>
            </>
          )}
        </section>
      </div>

      <section className="pay-panel">
        <div className="pay-panel__head" data-tour="payroll-band-assignments">
          <span className="admin-account-page__panel-icon"><FiUsers aria-hidden="true" /></span>
          <div style={{ flex: 1 }}>
            <h2>{t('Employee assignments')}</h2>
            <p>{t('Assign an employee to a band from a date. An individual rate override replaces the band rate but keeps its premium structure.')}</p>
          </div>
        </div>

        <div className="pay-form-grid" style={{ marginBottom: 16 }}>
          <label className="form__group">
            <span className="form__label">{t('Employee')}</span>
            <select
              className="form__input"
              value={assignGuardId}
              onChange={(event) => setAssignGuardId(event.target.value)}
            >
              <option value="">{t('Select employee')}</option>
              {assignableGuards.map((guard) => (
                <option key={guard.id} value={guard.id}>{guard.fullName}</option>
              ))}
            </select>
          </label>
          <label className="form__group">
            <span className="form__label">{t('Pay band')}</span>
            <select
              className="form__input"
              value={assignBandId}
              onChange={(event) => setAssignBandId(event.target.value)}
            >
              {bands.map((band) => (
                <option key={band.id} value={band.id}>{band.code} — {band.name}</option>
              ))}
            </select>
          </label>
          <label className="form__group">
            <span className="form__label">{t('Effective from')}</span>
            <input
              className="form__input"
              type="date"
              value={assignFrom}
              onChange={(event) => setAssignFrom(event.target.value)}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('Employment type')}</span>
            <select
              className="form__input"
              value={assignEmploymentType}
              onChange={(event) => setAssignEmploymentType(event.target.value as EmploymentType)}
            >
              {EMPLOYMENT_TYPES.map((type) => (
                <option key={type} value={type}>{t(EMPLOYMENT_TYPE_LABELS[type])}</option>
              ))}
            </select>
          </label>
          <label className="form__group">
            <span className="form__label">{t('Rate override (optional)')}</span>
            <input
              className="form__input"
              type="number"
              min="0"
              step="0.01"
              value={assignOverride}
              onChange={(event) => setAssignOverride(event.target.value)}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('Payroll reference')}</span>
            <input
              className="form__input"
              value={assignReference}
              onChange={(event) => setAssignReference(event.target.value)}
            />
          </label>
          <label className="form__group">
            <span className="form__label">{t('Cost centre')}</span>
            <input
              className="form__input"
              value={assignCostCentre}
              onChange={(event) => setAssignCostCentre(event.target.value)}
            />
          </label>
        </div>

        <div className="pay-inline-actions" style={{ marginBottom: 20 }}>
          <button type="button" className="btn btn--primary" onClick={assignEmployee} disabled={isSaving}>
            <FiPlus aria-hidden="true" />
            {t('Assign employee')}
          </button>
        </div>

        <div className="pay-table-wrap">
          <table className="pay-table">
            <thead>
              <tr>
                <th>{t('Employee')}</th>
                <th>{t('Band')}</th>
                <th>{t('Effective')}</th>
                <th className="pay-table__num">{t('Rate')}</th>
                <th>{t('Type')}</th>
                <th>{t('Reference')}</th>
                <th>{t('Cost centre')}</th>
              </tr>
            </thead>
            <tbody>
              {(selectedBandId ? assignmentsForBand : assignments).length === 0 ? (
                <tr><td colSpan={7} className="pay-empty">{t('No assignments yet.')}</td></tr>
              ) : (selectedBandId ? assignmentsForBand : assignments).map((assignment) => (
                <tr key={assignment.id}>
                  <td>{assignment.guardName}</td>
                  <td><span className="pay-chip pay-chip--band">{assignment.payBandCode}</span></td>
                  <td className="pay-table__muted">{formatDateRange(assignment.effectiveFrom, assignment.effectiveTo)}</td>
                  <td className="pay-table__money">
                    {assignment.hourlyRateOverride !== null
                      ? <>{formatMoney(assignment.hourlyRateOverride, currencyCode)} <span className="pay-chip pay-chip--warn">{t('Override')}</span></>
                      : <span className="pay-table__muted">{t('Band rate')}</span>}
                  </td>
                  <td>{t(EMPLOYMENT_TYPE_LABELS[assignment.employmentType] ?? assignment.employmentType)}</td>
                  <td className="pay-table__muted">{assignment.payrollReference ?? '-'}</td>
                  <td className="pay-table__muted">{assignment.costCentre ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
